import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Zap, Calendar as CalendarIcon, Download, Rss } from "lucide-react";
import Sidebar from "@/components/dashboard/Sidebar";
import { fetchSnipes, createSnipesBulk, fetchDropsFeed } from "@/lib/api";
import { useBranding } from "@/lib/branding";
import { useTimezone, formatInTz } from "@/lib/tz";
import AIDomainPicks from "@/components/dashboard/AIDomainPicks";

const REGISTRARS = ["transip", "spaceship", "dynadot", "hostingnl"];

/** Parse a plain-text or CSV block. Accepts:
 *   domain,YYYY-MM-DD HH:MM,registrar[,burst_intensity][,list_on_success][,price]
 *   domain (falls back to defaults)
 *   domain,YYYY-MM-DDTHH:MM:SSZ
 */
function parseBulkText(text, defaults) {
  const lines = text
    .split(/\n+/)
    .map((l) => l.trim())
    .filter((l) => l && !l.startsWith("#"));
  const out = [];
  const errors = [];
  for (const line of lines) {
    const parts = line.split(/[,;\t]/).map((p) => p.trim());
    const domain = (parts[0] || "").toLowerCase();
    if (!domain || !domain.includes(".")) {
      errors.push({ line, reason: "not a domain" });
      continue;
    }
    let iso = defaults.target_iso;
    if (parts[1]) {
      const raw = parts[1];
      const t = raw.includes("T") ? raw : raw.replace(" ", "T");
      const d = new Date(t.length <= 16 ? t + ":00Z" : t);
      if (!Number.isNaN(d.getTime())) iso = d.toISOString();
    }
    const registrar = (parts[2] || defaults.registrar || "transip").toLowerCase();
    if (!REGISTRARS.includes(registrar)) {
      errors.push({ line, reason: `unknown registrar '${registrar}'` });
      continue;
    }
    const intensity = Math.max(1, Math.min(3, Number(parts[3]) || defaults.burst_intensity || 2));
    const list_on_success = parts[4] ? /^(1|true|yes)$/i.test(parts[4]) : defaults.list_on_success;
    const lander_price = parts[5] ? Number(parts[5]) : null;
    out.push({
      domain,
      target_time: iso,
      registrar,
      retry_mode: "aggressive",
      retry_window_seconds: 60,
      retry_interval_ms: 200,
      parallel_attempts: 3,
      race_all_registrars: true,
      burst_intensity: intensity,
      list_on_success,
      lander_price,
    });
  }
  return { out, errors };
}

function BulkPanel({ onSubmitted }) {
  const nowPlusHour = useMemo(() => {
    const d = new Date();
    d.setHours(d.getHours() + 1);
    d.setSeconds(0, 0);
    return d.toISOString().slice(0, 16);
  }, []);
  const [defaultTime, setDefaultTime] = useState(nowPlusHour);
  const [defaultRegistrar, setDefaultRegistrar] = useState("transip");
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);

  const preview = useMemo(() => {
    const iso = new Date(defaultTime).toISOString();
    return parseBulkText(text, {
      target_iso: iso,
      registrar: defaultRegistrar,
      burst_intensity: 2,
      list_on_success: false,
    });
  }, [text, defaultTime, defaultRegistrar]);

  const submit = async () => {
    if (preview.out.length === 0) {
      toast.error("Nothing to schedule");
      return;
    }
    setBusy(true);
    try {
      const r = await createSnipesBulk(preview.out);
      toast.success(`${r.created} snipe(s) scheduled` + (r.errors?.length ? ` · ${r.errors.length} rejected` : ""));
      setText("");
      onSubmitted?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Bulk schedule failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="border border-[#eaecf0] p-5 bg-white" data-testid="bulk-schedule-panel">
      <div className="overline mb-3">Bulk schedule</div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
        <div>
          <label className="text-[10px] font-mono uppercase text-[#525252]">Default drop time</label>
          <input
            type="datetime-local"
            value={defaultTime}
            onChange={(e) => setDefaultTime(e.target.value)}
            data-testid="bulk-default-time"
            className="w-full mt-1 text-sm font-mono px-2 py-1.5 border border-[#eaecf0] rounded-sm"
          />
        </div>
        <div>
          <label className="text-[10px] font-mono uppercase text-[#525252]">Default registrar</label>
          <select
            value={defaultRegistrar}
            onChange={(e) => setDefaultRegistrar(e.target.value)}
            data-testid="bulk-default-registrar"
            className="w-full mt-1 text-sm font-mono px-2 py-1.5 border border-[#eaecf0] rounded-sm bg-white"
          >
            {REGISTRARS.map((r) => (
              <option key={r} value={r}>{r}</option>
            ))}
          </select>
          <p className="text-[10px] font-mono text-[#B45309] mt-1" data-testid="drops-tld-lock-note">
            .nl is routed to TransIP automatically
          </p>
        </div>
      </div>
      <textarea
        value={text}
        onChange={(e) => setText(e.target.value)}
        placeholder={`# One per line. Format:\n# domain,YYYY-MM-DD HH:MM,registrar,intensity,list,price\ncoolstartup.io\nanother.com,2026-08-04 12:00,dynadot,3,true,999\nquick.co,2026-08-04 13:15\n`}
        data-testid="bulk-schedule-textarea"
        rows={9}
        className="w-full text-sm font-mono px-3 py-2 border border-[#eaecf0] rounded-sm outline-none focus:border-[#03142f]"
      />
      <div className="flex items-center justify-between mt-3">
        <div className="text-[11px] font-mono text-[#525252]" data-testid="bulk-preview-count">
          <span className="text-[#047857]">{preview.out.length} ready</span>
          {preview.errors.length > 0 && (
            <>
              <span data-testid="bulk-preview-separator" className="mx-2 text-[#D4D4D8]">·</span>
              <span className="text-[#BE123C]" data-testid="bulk-preview-errors">
                {preview.errors.length} rejected
              </span>
            </>
          )}
        </div>
        <button
          onClick={submit}
          disabled={busy || preview.out.length === 0}
          data-testid="bulk-schedule-submit"
          className="text-[11px] font-mono uppercase px-4 py-2 bg-[#03142f] text-white hover:bg-[#0b274d] disabled:opacity-40"
        >
          <Zap className="w-3 h-3 inline mr-1.5" strokeWidth={2} />
          {busy ? "Scheduling…" : `Schedule ${preview.out.length}`}
        </button>
      </div>
      {preview.errors.length > 0 && (
        <div className="mt-3 text-[10px] font-mono text-[#BE123C]">
          {preview.errors.slice(0, 4).map((e, i) => (
            <div key={i} className="truncate">✗ {e.line} — {e.reason}</div>
          ))}
        </div>
      )}
    </div>
  );
}

function DropFeedPanel({ onScheduled }) {
  const [url, setUrl] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [selected, setSelected] = useState(new Set());
  const [target, setTarget] = useState(() => {
    const d = new Date();
    d.setHours(d.getHours() + 1);
    d.setSeconds(0, 0);
    return d.toISOString().slice(0, 16);
  });
  const [registrar, setRegistrar] = useState("transip");
  const [scheduling, setScheduling] = useState(false);

  const load = async (params) => {
    setBusy(true);
    setResult(null);
    setSelected(new Set());
    try {
      const r = await fetchDropsFeed(params);
      setResult(r);
      if (!r.count) toast.warning("No domains extracted from that URL");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Feed fetch failed");
    } finally {
      setBusy(false);
    }
  };

  const scheduleSelected = async () => {
    if (selected.size === 0) {
      toast.error("Select at least one domain");
      return;
    }
    setScheduling(true);
    try {
      const iso = new Date(target).toISOString();
      const snipes = Array.from(selected).map((d) => ({
        domain: d,
        target_time: iso,
        registrar,
        retry_mode: "aggressive",
        retry_window_seconds: 60,
        retry_interval_ms: 200,
        parallel_attempts: 3,
        race_all_registrars: true,
        burst_intensity: 2,
        list_on_success: false,
      }));
      const r = await createSnipesBulk(snipes);
      toast.success(`${r.created} snipe(s) scheduled from feed`);
      setSelected(new Set());
      onScheduled?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Schedule failed");
    } finally {
      setScheduling(false);
    }
  };

  const toggle = (d) => {
    const n = new Set(selected);
    n.has(d) ? n.delete(d) : n.add(d);
    setSelected(n);
  };

  return (
    <div className="border border-[#eaecf0] p-5 bg-white" data-testid="drop-feed-panel">
      <div className="overline mb-3">Drop feed · fetch dropping domains from any URL</div>
      <div className="flex gap-2 mb-3">
        <input
          value={url}
          onChange={(e) => setUrl(e.target.value)}
          placeholder="https://example.com/drop-list.csv  or  https://www.sidn.nl/…"
          data-testid="drop-feed-url"
          className="flex-1 text-sm font-mono px-3 py-2 border border-[#eaecf0] rounded-sm outline-none focus:border-[#03142f]"
        />
        <button
          onClick={() => load({ source: "custom", url, limit: 500 })}
          disabled={busy || !url}
          data-testid="drop-feed-fetch"
          className="text-[11px] font-mono uppercase px-4 py-2 border border-[#03142f] text-[#03142f] hover:bg-[#03142f] hover:text-white disabled:opacity-40"
        >
          <Download className="w-3 h-3 inline mr-1.5" strokeWidth={2} />
          Fetch
        </button>
        <button
          onClick={() => load({ source: "sidn", limit: 500 })}
          disabled={busy}
          data-testid="drop-feed-sidn"
          className="text-[11px] font-mono uppercase px-3 py-2 border border-[#eaecf0] text-[#525252] hover:bg-[#FAFAFA]"
        >
          <Rss className="w-3 h-3 inline mr-1.5" strokeWidth={2} />
          SIDN .nl
        </button>
      </div>
      {result && result.count > 0 && (
        <div className="mt-2 border-t border-[#eaecf0] pt-3">
          <div className="flex flex-wrap gap-2 items-end mb-3">
            <div>
              <label className="text-[10px] font-mono uppercase text-[#525252]">Target drop time</label>
              <input
                type="datetime-local"
                value={target}
                onChange={(e) => setTarget(e.target.value)}
                data-testid="feed-target-time"
                className="block mt-1 text-sm font-mono px-2 py-1.5 border border-[#eaecf0] rounded-sm"
              />
            </div>
            <div>
              <label className="text-[10px] font-mono uppercase text-[#525252]">Registrar</label>
              <select
                value={registrar}
                onChange={(e) => setRegistrar(e.target.value)}
                data-testid="feed-registrar"
                className="block mt-1 text-sm font-mono px-2 py-1.5 border border-[#eaecf0] rounded-sm bg-white"
              >
                {REGISTRARS.map((r) => <option key={r} value={r}>{r}</option>)}
              </select>
            </div>
            <div className="flex-1" />
            <button
              onClick={() => setSelected(new Set(result.domains))}
              data-testid="feed-select-all"
              className="text-[10px] font-mono uppercase px-3 py-1.5 border border-[#eaecf0] hover:bg-[#FAFAFA]"
            >
              Select all
            </button>
            <button
              onClick={scheduleSelected}
              disabled={scheduling || selected.size === 0}
              data-testid="feed-schedule-btn"
              className="text-[11px] font-mono uppercase px-4 py-1.5 bg-[#03142f] text-white hover:bg-[#0b274d] disabled:opacity-40"
            >
              <Zap className="w-3 h-3 inline mr-1.5" strokeWidth={2} />
              {scheduling ? "Scheduling…" : `Schedule ${selected.size || ""}`}
            </button>
          </div>
          <div
            data-testid="feed-domains-list"
            className="max-h-72 overflow-auto border border-[#eaecf0] divide-y divide-[#F4F4F5]"
          >
            {result.domains.map((d) => (
              <label
                key={d}
                className={`flex items-center gap-2 px-3 py-1.5 text-sm font-mono cursor-pointer hover:bg-[#FAFAFA] ${
                  selected.has(d) ? "bg-[#EEF2FF]" : ""
                }`}
              >
                <input
                  type="checkbox"
                  checked={selected.has(d)}
                  onChange={() => toggle(d)}
                  data-testid={`feed-row-${d}`}
                />
                <span>{d}</span>
              </label>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function CalendarPanel({ snipes, userTz }) {
  const days = useMemo(() => {
    const now = new Date();
    const start = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const arr = [];
    for (let i = 0; i < 30; i++) {
      const d = new Date(start);
      d.setDate(start.getDate() + i);
      arr.push(d);
    }
    return arr;
  }, []);

  const localDateKey = (iso) => {
    // Convert UTC ISO to "YYYY-MM-DD" in the user's timezone
    if (!iso) return "";
    try {
      const parts = new Intl.DateTimeFormat("en-CA", {
        timeZone: userTz,
        year: "numeric", month: "2-digit", day: "2-digit",
      }).formatToParts(new Date(iso));
      const y = parts.find((p) => p.type === "year")?.value;
      const m = parts.find((p) => p.type === "month")?.value;
      const d = parts.find((p) => p.type === "day")?.value;
      return `${y}-${m}-${d}`;
    } catch {
      return iso.slice(0, 10);
    }
  };

  const buckets = useMemo(() => {
    const b = {};
    for (const s of snipes || []) {
      if (s.status !== "scheduled") continue;
      const k = localDateKey(s.target_time);
      (b[k] ||= []).push(s);
    }
    return b;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [snipes, userTz]);

  return (
    <div className="border border-[#eaecf0] p-5 bg-white" data-testid="drop-calendar">
      <div className="overline mb-3 flex items-center justify-between">
        <span>Upcoming drops · next 30 days</span>
        <span className="text-[10px] normal-case tracking-normal font-mono text-[#A1A1AA]" data-testid="drops-tz">TZ · {userTz}</span>
      </div>
      <div className="grid grid-cols-7 gap-1">
        {["MO", "TU", "WE", "TH", "FR", "SA", "SU"].map((d) => (
          <div key={d} className="text-[9px] font-mono text-[#A1A1AA] text-center pb-1">{d}</div>
        ))}
        {days.map((d, idx) => {
          // Use the user's local date so calendar cells match Tooltip contents
          const key = (() => {
            try {
              const parts = new Intl.DateTimeFormat("en-CA", {
                timeZone: userTz,
                year: "numeric", month: "2-digit", day: "2-digit",
              }).formatToParts(d);
              const y = parts.find((p) => p.type === "year")?.value;
              const m = parts.find((p) => p.type === "month")?.value;
              const dd = parts.find((p) => p.type === "day")?.value;
              return `${y}-${m}-${dd}`;
            } catch {
              return d.toISOString().slice(0, 10);
            }
          })();
          const list = buckets[key] || [];
          const isFirst = idx === 0;
          const style = isFirst ? { gridColumnStart: ((d.getDay() + 6) % 7) + 1 } : {};
          const isToday = idx === 0;
          const heat = list.length === 0 ? "" : list.length < 3 ? "bg-[#DBEAFE]" : list.length < 6 ? "bg-[#BFDBFE]" : "bg-[#93C5FD]";
          return (
            <div
              key={key}
              style={style}
              data-testid={`cal-day-${key}`}
              className={`aspect-square border ${
                isToday ? "border-[#03142f]" : "border-[#F4F4F5]"
              } ${heat} p-1.5 rounded-sm flex flex-col`}
              title={list.map((s) => `${formatInTz(s.target_time, userTz, { hour: "2-digit", minute: "2-digit", year: undefined, month: undefined, day: undefined })} · ${s.domain}`).join("\n")}
            >
              <div className="text-[10px] font-mono text-[#525252]">{d.getDate()}</div>
              {list.length > 0 && (
                <div className="mt-auto text-[10px] font-mono font-semibold text-[#03142f] tabular-nums" data-testid={`cal-day-${key}-count`}>
                  {list.length}
                </div>
              )}
            </div>
          );
        })}
      </div>
      <div className="text-[10px] font-mono text-[#A1A1AA] mt-3">
        Hover any day to see the scheduled snipes. Numbers = count of drops that day.
      </div>
    </div>
  );
}

export default function Drops() {
  const brand = useBranding();
  const { userTz } = useTimezone();
  const [snipes, setSnipes] = useState([]);

  const load = async () => {
    try {
      const s = await fetchSnipes();
      setSnipes(s);
    } catch { /* silent */ }
  };
  useEffect(() => { load(); }, []);

  return (
    <div className="min-h-screen w-full flex bg-white" data-testid="drops-page">
      <Sidebar />
      <main className="flex-1 flex flex-col border-l border-[#eaecf0] min-w-0">
        <header className="h-14 border-b border-[#eaecf0] px-6 flex items-center gap-3">
          <span className="overline">{brand.brand_name}</span>
          <span className="text-[#D4D4D8]">/</span>
          <span className="text-sm font-medium flex items-center gap-2">
            <CalendarIcon className="w-4 h-4" strokeWidth={1.5} /> Drops
          </span>
        </header>
        <div className="flex-1 overflow-auto p-6 space-y-6">
          <AIDomainPicks onScheduled={load} />
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <BulkPanel onSubmitted={load} />
            <CalendarPanel snipes={snipes} userTz={userTz} />
          </div>
          <DropFeedPanel onScheduled={load} />
        </div>
      </main>
    </div>
  );
}
